import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import Confetti from 'react-confetti';
import { 
    CheckCircle2, 
    PartyPopper,
    ArrowRight,
    Home,
    FileText,
    Mail,
    Calendar,
    Briefcase,
    Star,
    Download,
    Share2
} from 'lucide-react';

const ThankYouPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [showConfetti, setShowConfetti] = useState(true);
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });

    const { paymentIntentId, orderDetails } = location.state || {};

    useEffect(() => {
        // Update window size for confetti
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };

        window.addEventListener('resize', handleResize);

        // Stop confetti after 5 seconds
        const timer = setTimeout(() => {
            setShowConfetti(false);
        }, 5000);

        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(timer);
        };
    }, []);

    useEffect(() => {
        // Redirect if no order details
        if (!orderDetails) {
            navigate('/');
        }
    }, [orderDetails, navigate]);

    if (!orderDetails) {
        return null;
    }

    const isSubscription = orderDetails.type === 'subscription';

    const getNextSteps = () => {
        if (isSubscription) {
            return [
                {
                    icon: <Briefcase className="w-5 h-5" />,
                    title: 'Browse Agencies',
                    description: 'Access full contact details for all recruitment agencies',
                    link: '/agencies',
                    buttonText: 'View Agencies'
                },
                {
                    icon: <FileText className="w-5 h-5" />,
                    title: 'Explore Jobs',
                    description: 'Browse and apply to thousands of job listings',
                    link: '/jobs',
                    buttonText: 'Find Jobs'
                },
                {
                    icon: <Calendar className="w-5 h-5" />,
                    title: 'Book Consultation',
                    description: 'Get personalized career advice from our experts',
                    link: '/consultation',
                    buttonText: 'Book Now'
                }
            ];
        }

        return [
            {
                icon: <FileText className="w-5 h-5" />,
                title: 'Complete Your Profile',
                description: 'Upload your CV and complete your job preferences',
                link: '/multi-apply',
                buttonText: 'Continue Setup'
            },
            {
                icon: <Briefcase className="w-5 h-5" />,
                title: 'Track Applications',
                description: 'Monitor the status of your job applications',
                link: '/dashboard',
                buttonText: 'View Dashboard'
            },
            {
                icon: <Mail className="w-5 h-5" />,
                title: 'Check Your Email',
                description: 'We\'ll send you updates on your applications',
                link: null,
                buttonText: null
            }
        ];
    };

    return (
        <Layout>
            {/* Confetti Effect */}
            {showConfetti && (
                <Confetti
                    width={windowSize.width}
                    height={windowSize.height}
                    recycle={false}
                    numberOfPieces={200}
                    gravity={0.3}
                    colors={['#0ea5e9', '#22c55e', '#f59e0b', '#ec4899', '#8b5cf6']}
                />
            )}

            <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-primary-50 py-12">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Success Header */}
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6 animate-bounce">
                            <CheckCircle2 className="w-10 h-10 text-green-600" />
                        </div>
                        
                        <div className="flex items-center justify-center gap-2 mb-4">
                            <PartyPopper className="w-6 h-6 text-amber-500" />
                            <Badge className="bg-green-100 text-green-700 text-sm px-3 py-1">
                                Payment Successful
                            </Badge>
                            <PartyPopper className="w-6 h-6 text-amber-500 transform scale-x-[-1]" />
                        </div>

                        <h1 className="font-heading text-3xl md:text-4xl font-bold text-neutral-900 mb-3">
                            Thank You for Your Purchase!
                        </h1>
                        
                        <p className="text-lg text-neutral-600 max-w-lg mx-auto">
                            {isSubscription 
                                ? 'Your subscription is now active. You have full access to all premium features.'
                                : 'Your Multi-Apply package is ready. Our team will start working on your applications.'}
                        </p>
                    </div>

                    {/* Order Confirmation Card */}
                    <Card className="mb-8 border-green-200 bg-white shadow-lg">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="font-semibold text-neutral-900">Order Confirmation</h2>
                                <Badge variant="outline" className="text-green-600 border-green-300">
                                    Confirmed
                                </Badge>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <p className="text-neutral-500">Package</p>
                                    <p className="font-medium text-neutral-900">{orderDetails.name}</p>
                                </div>
                                <div>
                                    <p className="text-neutral-500">Amount Paid</p>
                                    <p className="font-medium text-neutral-900">
                                        £{(orderDetails.amount / 100).toFixed(2)}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-neutral-500">Type</p>
                                    <p className="font-medium text-neutral-900 capitalize">
                                        {isSubscription ? 'Monthly Subscription' : 'One-time Purchase'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-neutral-500">Status</p>
                                    <p className="font-medium text-green-600 flex items-center gap-1">
                                        <CheckCircle2 className="w-4 h-4" />
                                        Active
                                    </p>
                                </div>
                            </div>

                            {paymentIntentId && (
                                <div className="mt-4 pt-4 border-t border-neutral-100">
                                    <p className="text-xs text-neutral-400">
                                        Reference: {paymentIntentId}
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* What's Next Section */}
                    <div className="mb-8">
                        <h2 className="text-xl font-semibold text-neutral-900 mb-4 text-center">
                            What's Next?
                        </h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {getNextSteps().map((step, idx) => (
                                <Card key={idx} className="hover:shadow-md transition-shadow">
                                    <CardContent className="p-5 text-center">
                                        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3 text-primary-600">
                                            {step.icon}
                                        </div>
                                        <h3 className="font-medium text-neutral-900 mb-1">
                                            {step.title}
                                        </h3>
                                        <p className="text-sm text-neutral-500 mb-3">
                                            {step.description}
                                        </p>
                                        {step.link && (
                                            <Link to={step.link}>
                                                <Button 
                                                    variant="outline" 
                                                    size="sm"
                                                    className="w-full"
                                                >
                                                    {step.buttonText}
                                                    <ArrowRight className="w-4 h-4 ml-1" />
                                                </Button>
                                            </Link>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>

                    {/* Email Notice */}
                    <Card className="bg-primary-50 border-primary-100 mb-8">
                        <CardContent className="p-5 flex items-center gap-4">
                            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center shrink-0">
                                <Mail className="w-5 h-5 text-primary-600" />
                            </div>
                            <div>
                                <h3 className="font-medium text-neutral-900">
                                    Confirmation Email Sent
                                </h3>
                                <p className="text-sm text-neutral-600">
                                    We've sent a receipt and confirmation details to your email address.
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link to="/dashboard">
                            <Button className="bg-primary-600 hover:bg-primary-700 text-white px-8">
                                Go to Dashboard
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                        <Link to="/">
                            <Button variant="outline" className="px-8">
                                <Home className="w-4 h-4 mr-2" />
                                Return Home
                            </Button>
                        </Link>
                    </div>

                    {/* Support Note */}
                    <p className="text-center text-sm text-neutral-500 mt-8">
                        Need help? Contact us at{' '}
                        <a href="mailto:support@recruitnearme.co.uk" className="text-primary-600 hover:underline">
                            support@recruitnearme.co.uk
                        </a>
                    </p>
                </div>
            </div>
        </Layout>
    );
};

export default ThankYouPage;
